/**
 * Purpose:
 *   Define the permission keys issued in authenticated access tokens.
 *
 * Notes:
 *   Kept runtime-agnostic so route protection can use it from the proxy.
 */

export const PERMISSIONS = {
  accounts: {
    create: 'accounts.create',
    read: 'accounts.read',
    list: 'accounts.list',
    update: 'accounts.update',
    delete: 'accounts.delete',
  },
  paymentMethods: {
    create: 'payment-methods.create',
    read: 'payment-methods.read',
    list: 'payment-methods.list',
    update: 'payment-methods.update',
    delete: 'payment-methods.delete',
    setDefault: 'payment-methods.set-default',
  },
  paymentRecords: {
    create: 'payment-records.create',
    read: 'payment-records.read',
    list: 'payment-records.list',
  },
  productOfferings: {
    read: 'product-offerings.read',
    list: 'product-offerings.list',
    update: 'product-offerings.update',
  },
  subscriptions: {
    read: 'subscriptions.read',
    list: 'subscriptions.list',
    update: 'subscriptions.update',
    activate: 'subscriptions.activate',
    suspend: 'subscriptions.suspend',
    retire: 'subscriptions.retire',
  },
  tags: {
    create: 'tags.create',
    list: 'tags.list',
    update: 'tags.update',
    delete: 'tags.delete',
    assign: 'tags.assign',
    unassign: 'tags.unassign',
  },
  users: {
    create: 'users.create',
    read: 'users.read',
    list: 'users.list',
    update: 'users.update',
    delete: 'users.delete',
  },
  backgroundOperations: {
    cancel: 'background-operations.cancel',
    list: 'background-operations.list',
    read: 'background-operations.read',
    retry: 'background-operations.retry',
  },
} as const;

export type TKnownPermission =
  (typeof PERMISSIONS)[keyof typeof PERMISSIONS][keyof (typeof PERMISSIONS)[keyof typeof PERMISSIONS]];

export function hasPermission(
  permissions: readonly string[] | null | undefined,
  permission: string
): boolean {
  return permissions?.includes(permission) ?? false;
}
